/**
 * Egress target picker (kind + value) with autocomplete from observed traffic.
 *
 * The "value" field shows datalist suggestions fed by
 * ``/rate-limits/discovered-targets`` — so admins pick from destinations
 * the system has actually called rather than having to type
 * ``api.acme.com`` from memory. Free-text is allowed for pre-configuring
 * a target before any traffic flows.
 */
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  rateLimitsApi,
  type EgressTargetKind,
} from "@/lib/api-rate-limits";
import { cn } from "@/lib/cn";


export interface EgressTargetInputProps {
  kind: EgressTargetKind;
  onKindChange: (k: EgressTargetKind) => void;
  value: string;
  onValueChange: (v: string) => void;
  disabled?: boolean;
}


export function EgressTargetInput({
  kind,
  onKindChange,
  value,
  onValueChange,
  disabled,
}: EgressTargetInputProps) {
  const q = useQuery({
    queryKey: ["rate-limits", "discovered-targets", kind],
    queryFn: () => rateLimitsApi.discoveredTargets({ kind, limit: 100 }),
    staleTime: 30_000,
  });

  const listId = React.useId();
  const suggestions = q.data ?? [];

  return (
    <div className="space-y-2">
      <div className="space-y-1.5">
        <Label>Target kind</Label>
        <div className="flex gap-1.5">
          {(["http_host", "connector"] as const).map((k) => (
            <button
              key={k}
              type="button"
              disabled={disabled}
              onClick={() => onKindChange(k)}
              className={cn(
                "rounded-md border px-2.5 py-1 text-xs",
                kind === k
                  ? "border-accent bg-accent/10 text-foreground"
                  : "border-border bg-surface text-muted-foreground hover:border-muted-foreground",
                disabled && "opacity-50",
              )}
            >
              {k === "http_host" ? "HTTP host" : "Connector"}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="egress-target-value">
          Target {kind === "http_host" ? "hostname" : "instance"}
        </Label>
        <Input
          id="egress-target-value"
          value={value}
          onChange={(e) => onValueChange(e.target.value.trim().toLowerCase())}
          placeholder={
            kind === "http_host"
              ? "api.acme.com"
              : "sap_order_push:prd-erp"
          }
          list={listId}
          disabled={disabled}
        />
        <datalist id={listId}>
          {suggestions.map((s) => (
            <option key={`${s.kind}:${s.value}`} value={s.value}>
              {s.deliveries_count > 0
                ? `${s.deliveries_count} deliveries observed`
                : ""}
            </option>
          ))}
        </datalist>
        <p className="text-[11px] text-muted-foreground">
          {suggestions.length > 0
            ? `${suggestions.length} target${suggestions.length === 1 ? "" : "s"} observed in traffic — start typing to filter.`
            : "No targets observed yet — type the hostname manually."}
        </p>
      </div>
    </div>
  );
}
